/**
 * 분석 결과 표시 정책 (T2 · 2026-08-14 중앙대 회의 후속)
 *
 * 2026-08-14 회신 메일:
 *
 * > 현재 표시되는 백분율은 JSD 거리를 저희가 환산한 값으로, 정답 확률이나
 * > 검증된 신뢰도와는 다른 개념입니다. AI 노트도 내부 규칙에 따른 문구입니다.
 * > 따라서 두 표시는 우선 제외하고, 과제 내 표시 기준이 정리되는 대로 그
 * > 결과에 맞춰 반영하겠습니다.
 *
 * 값을 지우지 않는다. 백엔드 계약(`matching_score` / `confidence_score` /
 * `ai_note`)과 저장된 데이터는 그대로 두고 **표시만** 멈춘다. 값을 지우면
 * 되돌리기 어렵고 이미 생성된 보고서 스냅샷과도 어긋난다.
 *
 * 기준이 확정되면 해당 플래그만 되돌린다. 표시를 되살릴 때는 백엔드
 * 보고서 출력(`app/services/report_generator.py` 의 동일 정책 상수)도 함께
 * 되돌려야 화면과 보고서가 어긋나지 않는다.
 */

/**
 * 매칭/신뢰도 백분율 표시 여부.
 *
 * JSD 거리를 환산한 값이라 "정답 확률"로 읽히면 안 된다. 과제 내 표시 기준
 * 확정 전까지 보류한다. JSD 거리 자체(`jsd_distance`)는 환산값이 아니라
 * 모델 출력 그대로이므로 계속 표시한다 — 표시 방식은 인하대와 협의 중인
 * 별건이다.
 */
export const SHOW_MATCH_PERCENTAGE = false;

/**
 * AI 노트 표시 여부.
 *
 * 내부 규칙으로 생성한 문구라 검증된 소견으로 읽히면 안 된다.
 */
export const SHOW_AI_NOTE = false;

/**
 * JSD 거리에 붙이는 해석 라벨('신뢰 높음' / '확인 필요' / '매칭 약함') 표시 여부.
 *
 * 2026-08-27 추가. 백분율·AI 노트를 뺀 뒤에도 이 라벨이 남아 있었다 — 경계값
 * 0.2 / 0.5 는 **우리가 임의로 정한 값**이고 '신뢰 높음'은 판정어라, 8/14 회의에서
 * 문제가 된 "검증되지 않은 환산 표시"와 성질이 같다. 2026-08-27 회신에 "JSD 거리만
 * 표시합니다" 라고 밝힌 것과도 어긋나 있었다.
 *
 * **JSD 거리 원값(0.000 형식)은 계속 표시한다** — 모델이 낸 값 그대로이기 때문이다.
 * 감추는 것은 그 값에 우리가 붙인 해석뿐이다. 인하대가 신뢰도 척도를 정의하면
 * 그 기준으로 이 플래그를 되살린다(경계값도 그때 정의된 값으로 교체할 것).
 */
export const SHOW_JSD_LABEL = false;

/**
 * KROVEX 증기압 예측값에 더하던 보정(+2.0 log Pa)과 그 스케일의 휘발성 구간
 * ('고휘발' / '중간' / '저휘발') 표시 여부.
 *
 * 2026-08-28 추가. 실측 조사 결과 이 보정에는 근거가 없다:
 *
 * - 상수는 프론트 3개 파일에만 있었고 백엔드·AI mock·인하대 원본 어디에도
 *   대응 보정이 없다. DB·API·보고서는 모두 **원값**을 쓴다.
 * - 도입 커밋(`3c39bcb`, 2026-03-19)의 근거는 주석 두 줄 "KROVEX 모델은
 *   증기압을 체계적으로 약 2 log Pa 낮게 예측합니다"뿐이고 출처·측정이 없다.
 *   유일한 문서 언급(`docs/dashboard-iter2.1-eval.md:106,161`)은 정당화가
 *   아니라 "출처/uncertainty 미노출"이라는 **미해결 결함 지적**이다.
 * - 학습 타깃(`backend/inha_models/datasets/vp.csv`, 인하대 원본과 바이트
 *   동일)의 값이 펜탄 2.71·헥산 2.18·옥탄 1.15 등에서 25 °C **log10(mmHg)**
 *   문헌값과 일치한다. log10(133.322)=2.125 — "2 낮음"의 정체는 모델 편향이
 *   아니라 mmHg→Pa 단위 미변환으로 보인다.
 * - 다만 같은 파일에 스케일이 다른 두 블록이 섞여 있어(교차 중복 200쌍의
 *   오프셋 평균 1.20·표준편차 0.53) **모델 출력의 단위 자체가 정의되지
 *   않는다**. 어떤 단일 상수도 전 구간을 맞출 수 없다.
 * - 구간 경계(보정 3.5/2.0)도 우리가 정한 값이고, 백엔드는 같은 값에 다른
 *   임계(원값 3.0/1.0)를 쓴다.
 *
 * 그래서 JSD 라벨과 같은 처리를 한다 — **모델이 낸 원값은 계속 표시하고,
 * 그 값에 우리가 붙인 보정·구간 해석만 감춘다.** 원값 표시는 DB·API·보고서와
 * 일치한다는 이점도 있다. 인하대가 단위와 보정 기준을 확인해 주면 이 플래그를
 * 되살린다(그때 상수 `VP_CORRECTION_LOG_PA` 와 구간 경계도 확인된 값으로
 * 교체할 것). 상수·헬퍼는 `src/utils/vaporPressure.ts` 에 모여 있다.
 */
export const SHOW_VP_CORRECTION = false;

/**
 * RoMSH 입력 성분 표(카드 '자세히') · 분석 상세 EvidenceCard 「RoMSH 입력 기록」 줄
 * 표시 여부 — PLAN 2-9a (docs/2026/260923_RoMSH_입력기록_기획.md §3-1).
 *
 * 2026-09-24 추가(기본 false) → **2026-09-26 운영자 결정으로 켬.** 기획 §1-3 의
 * 켜기 전 조건(관리자 [AI 모델] 화면·EvidenceCard 제목 문구 정정, 카드 '우세 후보
 * 단서' 출처 표기, 인하대 실무진 비공개 1회)은 운영자 판단으로 생략했다(D1~D20
 * 로컬 대조는 2026-09-25 완료). 값은 테스트로 고정한다 — 바꿀 때는 그 테스트를
 * 함께 바꾸는 별도 변경으로 한다.
 *
 * 백엔드는 이 플래그와 무관하게 새 분석부터 기록을 쌓는다. 끄면 표·EvidenceCard
 * 줄만 사라지고 기록은 그대로다(롤백은 이 값을 false 로 되돌리는 frontend 배포).
 */
export const SHOW_ROMSH_INPUTS = true;

/** 보류 중인 표시 자리에 쓰는 문자열. 빈칸으로 두면 결측과 구분되지 않는다. */
export const WITHHELD_PLACEHOLDER = "—";
